import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

/* Every item is a real page. The one page era steered these at hash anchors
   inside a fifteen thousand pixel scroll, and the client's verdict on that was
   "I don't know where to find what." The nav is now the site map. */
const NAV_LINKS = [
  { label: "Work", to: "/work", id: "nav-work" },
  { label: "Services", to: "/services", id: "nav-services" },
  { label: "Stay", to: "/stay", id: "nav-stay" },
  { label: "About", to: "/about", id: "nav-about" },
];

const MOBILE_LINKS = [...NAV_LINKS, { label: "Contact", to: "/contact", id: "nav-contact" }];

export const Nav = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  /**
   * The header is never fully transparent. design_guidelines.json is explicit
   * about this, and two real bugs proved it: on venture pages the nav rendered
   * ink on near black at about 1.03:1 and vanished entirely, and on the home
   * hero the right hand links sat unreadably on top of the photograph.
   *
   * So the bar is always glass, and the glass takes the tone of what it sits
   * over. Dark glass on the dark venture heroes, cream glass everywhere else.
   */
  const darkContext = location.pathname.startsWith("/ventures/");
  const barTone = darkContext
    ? "bg-ink/70 backdrop-blur-xl border-cream/15"
    : "bg-cream/80 backdrop-blur-xl border-ink/15";
  const linkTone = darkContext
    ? "text-cream/85 hover:text-cream"
    : "text-ink";
  const ctaTone = darkContext
    ? "border-cream/40 text-cream hover:bg-cream hover:text-ink"
    : "border-ink text-ink hover:bg-ink hover:text-cream";
  const onDarkHero = darkContext;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      {/* Plain header with a CSS entrance, not motion.header. See .nav-enter
          in index.css: framer applied the initial offset as an inline style, so
          a framer failure would have left the site's only navigation parked
          above the viewport at zero opacity. CSS has no such dependency. */}
      <header
        data-testid="site-nav"
        className={`nav-enter fixed top-0 left-0 right-0 z-50 transition-colors duration-500 border-b ${barTone} ${
          scrolled ? "" : "border-transparent"
        }`}
      >
        <div className="mx-auto max-w-[1600px] px-6 md:px-10 h-[76px] flex items-center justify-between">
          {/* Identity lockup: her face, then the wordmark.
              She is the brand, so the mark is her. Uses the pre-cropped
              portrait, so the mark is a real headshot rather than a scaled and
              offset slice of a larger frame. A face is far more legible at
              42px than the same photograph was at hero scale, which is where
              its resolution actually showed.
              The name is small caps at wide tracking, which is what separates
              a mark from running text and what lets it survive being small. */}
          <Link
            to="/"
            className="group/logo flex items-center gap-3 md:gap-3.5 shrink-0"
            data-testid="nav-logo"
            aria-label="Shruthi Ravi, home"
            data-cursor="hover"
          >
            <span
              className={`relative block w-[38px] h-[38px] md:w-[42px] md:h-[42px] rounded-full overflow-hidden shrink-0 ring-1 transition-all duration-500 ${
                onDarkHero
                  ? "ring-cream/25 group-hover/logo:ring-cream/60"
                  : "ring-ink/15 group-hover/logo:ring-ink/45"
              }`}
            >
              <img
                src="/portfolio/img/shruthi-portrait-crop.jpg"
                alt=""
                aria-hidden="true"
                width={720}
                height={900}
                className="absolute inset-0 w-full h-full object-cover"
                style={{ objectPosition: "50% 18%" }}
                data-testid="nav-logo-portrait"
              />
            </span>

            <span className="block">
              <span
                className={`block font-display uppercase leading-none whitespace-nowrap text-[15px] md:text-[17px] transition-colors duration-500 ${
                  onDarkHero ? "text-cream" : "text-ink"
                }`}
                style={{ letterSpacing: "0.19em", fontWeight: 500 }}
              >
                Shruthi Ravi
              </span>
              <span
                className={`mt-[7px] flex items-center gap-2 transition-colors duration-500 ${
                  onDarkHero ? "text-cream/55" : "text-ink2"
                }`}
              >
                <span
                  aria-hidden="true"
                  className={`h-px w-4 transition-all duration-500 group-hover/logo:w-7 ${
                    onDarkHero ? "bg-cream/40" : "bg-ink/30"
                  }`}
                />
                <span
                  className="font-mono uppercase leading-none text-[8px] md:text-[9px]"
                  /* Explicit 500. Body sets font-weight 300, and mono at 9px
                     inherits it as something that reads as a smudge. */
                  style={{ letterSpacing: "0.3em", fontWeight: 500 }}
                >
                  Architecture · Interiors
                </span>
              </span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-8 lg:gap-10">
            {NAV_LINKS.map((l) => (
              <Link
                key={l.id}
                to={l.to}
                data-testid={l.id}
                data-cursor="hover"
                className={`label editorial-link transition-colors duration-500 ${linkTone}`}
              >
                {l.label}
              </Link>
            ))}
          </nav>

          <div className="hidden lg:block">
            <Link
              to="/contact"
              data-testid="nav-cta"
              data-cursor="hover"
              className={`inline-block px-5 py-2.5 border label transition-colors duration-500 ${ctaTone}`}
            >
              Start a project
            </Link>
          </div>

          <button
            type="button"
            aria-label="Toggle menu"
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((s) => !s)}
            className={`lg:hidden transition-colors duration-500 ${
              onDarkHero ? "text-cream" : "text-ink"
            }`}
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </header>

      {/* Mobile fullscreen menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-cream lg:hidden pt-24 px-6 overflow-y-auto"
            data-testid="mobile-menu"
          >
            <ul className="flex flex-col gap-6 mt-6">
              {MOBILE_LINKS.map((l, i) => (
                <motion.li
                  key={l.id}
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.05 * i, duration: 0.4 }}
                >
                  <Link
                    to={l.to}
                    onClick={() => setOpen(false)}
                    className="block font-display text-5xl text-ink"
                    data-testid={`mobile-${l.id}`}
                  >
                    {l.label}
                  </Link>
                </motion.li>
              ))}
            </ul>
            <div className="mt-16 label text-ink2 space-y-1">
              <p>shruthiravioli@gmail.com</p>
              <p>+1 312 868 2030</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
